import { useEffect, useState } from 'react';
import axios from 'axios';

/* Customer accounts inside one tenant (TEC). One shared store for the console:
   the customer list, which customer owns what (tags) and the "Managing
   customer" selection that filters Users, Extensions, DIDs, Queues, IVR and
   Business Hours — like the Managing-site switcher. */
type Cfg = { enabled: boolean; customers: any[]; blocks: any[]; tags: Record<string, Record<string, number>>; org?: { name: string; digits: number; ext_lo: number; ext_hi: number; currency: string; timezone: string } };
const EMPTY: Cfg = { enabled: false, customers: [], blocks: [], tags: {} };
const KEY = 'ce_cust_sel';
let state: { cfg: Cfg; sel: string } = { cfg: EMPTY, sel: (() => { try { return localStorage.getItem(KEY) || 'all'; } catch { return 'all'; } })() };
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

export function loadCustomers() {
  return axios.get('/api/customers/config')
    .then((r) => { state = { ...state, cfg: r.data && r.data.enabled ? r.data : EMPTY }; emit(); })
    .catch(() => { state = { ...state, cfg: EMPTY }; emit(); });
}
// The organisation that owns the customers (TEC, or another tenant with customer accounts — 2026-10-29)
export function orgName() { return (state.cfg.org && state.cfg.org.name) || 'The Executive Centre'; }
export function orgCurrency() { return (state.cfg.org && state.cfg.org.currency) || 'AED'; }
export function orgTimezone() { return (state.cfg.org && state.cfg.org.timezone) || 'Asia/Dubai'; }
// TEC numbers customers with 4-digit extensions from their numbers; other organisations use blocks of their own 6-digit range
export function orgRange(): { digits: number; lo: number; hi: number } {
  const o: any = state.cfg.org || {};
  return Number(o.digits) === 6 ? { digits: 6, lo: Number(o.ext_lo), hi: Number(o.ext_hi) } : { digits: 4, lo: 100, hi: 9999 };
}
export function setCustomerSel(v: string) {
  state = { ...state, sel: v || 'all' };
  try { localStorage.setItem(KEY, state.sel); } catch { /* storage off */ }
  emit();
}

export function useCustomer() {
  const [, force] = useState(0);
  useEffect(() => { const f = () => force((x) => x + 1); subs.add(f); return () => { subs.delete(f); }; }, []);
  const { cfg } = state;
  const sel = cfg.enabled ? state.sel : 'all';
  const selected = sel !== 'all' && sel !== 'house' ? (cfg.customers.find((c: any) => String(c.id) === sel) || null) : null;
  const tagOf = (entity: string, id: any) => ((cfg.tags[entity] || {})[String(id)] || null);
  // strict: only the selected customer's items (house view: only untagged)
  const match = (entity: string, id: any) => {
    if (!cfg.enabled || sel === 'all') return true;
    const t = tagOf(entity, id);
    return sel === 'house' ? !t : String(t) === sel;
  };
  // shared: the selected customer's items plus the tenant's own (untagged) ones
  const matchShared = (entity: string, id: any) => {
    if (!cfg.enabled || sel === 'all' || sel === 'house') return match(entity, id);
    const t = tagOf(entity, id);
    return !t || String(t) === sel;
  };
  const nameOf = (cid: any) => { const c = cfg.customers.find((x: any) => String(x.id) === String(cid)); return c ? c.name : ''; };
  return {
    enabled: cfg.enabled, cfg, sel, selected, tagOf, match, matchShared, nameOf, org: orgName(),
    customerId: selected ? selected.id : null,
    prefix: selected ? String(selected.code).toLowerCase() + '-' : '',
    setSel: setCustomerSel, reload: loadCustomers,
  };
}

// Apply the prefix once (new items only): "main-menu" -> "acme-main-menu"
export function withPrefix(prefix: string, name: string) {
  const n = String(name || '').trim();
  return prefix && n && !n.toLowerCase().startsWith(prefix) ? prefix + n : n;
}

export function CustomerBar() {
  const c = useCustomer();
  if (!c.enabled) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 14 }}>
      <span style={{ fontSize: 13, color: '#6b7280' }}>Managing customer:</span>
      <select value={c.sel} onChange={(e) => c.setSel(e.target.value)}
        style={{ padding: '8px 12px', border: '1px solid #cdd2e6', borderRadius: 8, fontWeight: 600, background: c.sel === 'all' ? '#fff' : '#fff7ed' }}>
        <option value="all">All customers</option>
        <option value="house">{c.org} (own)</option>
        {c.cfg.customers.map((x: any) => <option key={x.id} value={String(x.id)}>{x.name} ({x.code}){x.status !== 'active' ? ' — ' + x.status : ''}</option>)}
      </select>
    </span>
  );
}

// Destination lists (IVR / hours / holidays) for the selected customer: its own
// extensions; its own plus the tenant's shared queues, menus, rules and greetings.
export function scopeToCustomer(data: any, c: any) {
  if (!c || !c.enabled || c.sel === 'all') return data;
  return {
    ...data,
    extensions: (data.extensions || []).filter((x: any) => c.match('extension', x.id)),
    queues: (data.queues || []).filter((x: any) => c.matchShared('queue', x.id)),
    ivrs: (data.ivrs || []).filter((x: any) => c.matchShared('ivr', x.id)),
    tcs: (data.tcs || []).filter((x: any) => c.matchShared('time_condition', x.id)),
    prompts: (data.prompts || []).filter((x: any) => c.matchShared('prompt', x.id)),
  };
}
